import { useState, useEffect } from 'react';
import { Plus, Copy, Check, Trash2, Users } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import toast from 'react-hot-toast';

interface InviteCode {
  id: string;
  code: string;
  createdAt: string;
  usedAt?: string;
  usedBy?: {
    id: string;
    username: string;
    displayName: string;
    avatar?: string;
  };
}

interface RateLimitInfo {
  isAdmin?: boolean;
  unlimited?: boolean;
  currentCount: number;
  limit: number | null;
  remaining: number | null;
  nextAvailableAt: string | null;
}

export default function InviteCodeManager() {
  const token = useAuthStore((state) => state.token)!;
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    loadInviteCodes();
  }, []);

  async function loadInviteCodes() {
    try {
      const response = await fetch('/api/invites', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to load invite codes');

      const data = await response.json();
      setInviteCodes(data.inviteCodes);
      setRateLimitInfo(data.rateLimitInfo);
    } catch (error) {
      toast.error('Failed to load invite codes');
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      const response = await fetch('/api/invites', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate invite code');
      }

      const data = await response.json();
      setInviteCodes([data.inviteCode, ...inviteCodes]);
      toast.success('New invite code generated!');
      // Reload to get updated rate limit info
      await loadInviteCodes();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate invite code');
    } finally {
      setGenerating(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this unused invite code?')) return;

    try {
      const response = await fetch(`/api/invites/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete');
      }

      setInviteCodes(inviteCodes.filter(code => code.id !== id));
      toast.success('Invite code deleted');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete invite code');
    }
  }

  function handleCopy(code: string, id: string) {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    toast.success('Invite code copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  }

  function getDaysUntilReset(): number | null {
    if (!rateLimitInfo?.nextAvailableAt) return null;
    const now = new Date();
    const resetDate = new Date(rateLimitInfo.nextAvailableAt);
    const diffTime = resetDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  function getStatusColor(): string {
    if (!rateLimitInfo || rateLimitInfo.unlimited) return 'blue';
    if (rateLimitInfo.remaining === 0) return 'red';
    if (rateLimitInfo.remaining === 1) return 'yellow';
    return 'green';
  }

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="bg-canvas border border-edge rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Invite Codes</h2>
          <p className="text-sm text-ink-muted">Generate codes to invite new users to this instance</p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating || (!rateLimitInfo?.unlimited && rateLimitInfo?.remaining === 0)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
          {generating ? 'Generating...' : 'Generate Code'}
        </button>
      </div>

      {rateLimitInfo?.unlimited && (
        <div className="mb-4 p-4 rounded-lg border-2 bg-purple-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium mb-1 text-purple-900">
                Administrator - Unlimited Invites
              </h3>
              <p className="text-sm text-purple-700">
                As an administrator, you can generate unlimited invite codes
              </p>
            </div>
            <div className="text-right ml-4">
              <div className="text-3xl font-bold text-purple-600">
                ∞
              </div>
              <div className="text-xs text-purple-600">
                unlimited
              </div>
            </div>
          </div>
        </div>
      )}

      {rateLimitInfo && rateLimitInfo.limit && rateLimitInfo.limit > 0 && !rateLimitInfo.unlimited && (
        <div className={`mb-4 p-4 rounded-lg border-2 ${
          getStatusColor() === 'green' ? 'bg-green-50 border-green-200' :
          getStatusColor() === 'yellow' ? 'bg-yellow-50 border-yellow-200' :
          getStatusColor() === 'red' ? 'bg-red-50 border-red-200' :
          'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className={`text-sm font-medium mb-1 ${
                getStatusColor() === 'green' ? 'text-green-900' :
                getStatusColor() === 'yellow' ? 'text-yellow-900' :
                getStatusColor() === 'red' ? 'text-red-900' :
                'text-blue-900'
              }`}>
                Weekly Invite Limit
              </h3>
              <p className={`text-sm ${
                getStatusColor() === 'green' ? 'text-green-700' :
                getStatusColor() === 'yellow' ? 'text-yellow-700' :
                getStatusColor() === 'red' ? 'text-red-700' :
                'text-blue-700'
              }`}>
                {(rateLimitInfo.remaining ?? 0) > 0 ? (
                  <>
                    You can generate <strong>{rateLimitInfo.remaining}</strong> more invite code{rateLimitInfo.remaining !== 1 ? 's' : ''} this week
                  </>
                ) : (
                  <>You have reached your weekly limit</>
                )}
              </p>
              {getDaysUntilReset() !== null && (
                <p className={`text-xs mt-1 ${
                  getStatusColor() === 'green' ? 'text-green-600' :
                  getStatusColor() === 'yellow' ? 'text-yellow-600' :
                  getStatusColor() === 'red' ? 'text-red-600' :
                  'text-blue-600'
                }`}>
                  {getDaysUntilReset()! > 0 ? (
                    <>Resets in {getDaysUntilReset()} day{getDaysUntilReset()! !== 1 ? 's' : ''}</>
                  ) : (
                    <>Resets today</>
                  )}
                </p>
              )}
            </div>
            <div className="text-right ml-4">
              <div className={`text-3xl font-bold ${
                getStatusColor() === 'green' ? 'text-green-600' :
                getStatusColor() === 'yellow' ? 'text-yellow-600' :
                getStatusColor() === 'red' ? 'text-red-600' :
                'text-blue-600'
              }`}>
                {rateLimitInfo.currentCount}/{rateLimitInfo.limit}
              </div>
              <div className={`text-xs ${
                getStatusColor() === 'green' ? 'text-green-600' :
                getStatusColor() === 'yellow' ? 'text-yellow-600' :
                getStatusColor() === 'red' ? 'text-red-600' :
                'text-blue-600'
              }`}>
                used this week
              </div>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                getStatusColor() === 'green' ? 'bg-green-500' :
                getStatusColor() === 'yellow' ? 'bg-yellow-500' :
                getStatusColor() === 'red' ? 'bg-red-500' :
                'bg-blue-500'
              }`}
              style={{ width: `${(rateLimitInfo.currentCount / rateLimitInfo.limit) * 100}%` }}
            />
          </div>
        </div>
      )}

      {inviteCodes.length === 0 ? (
        <div className="text-center py-12 px-6 bg-gray-50 rounded-lg border border-edge">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-ink mb-2">
            No invite codes yet
          </h3>
          <p className="text-ink-muted mb-4">
            Generate your first invite code to invite new users
          </p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Generate Code
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {inviteCodes.map((inviteCode) => (
            <div
              key={inviteCode.id}
              className={`p-4 rounded-lg border-2 ${
                inviteCode.usedAt
                  ? 'border-gray-200 bg-gray-50'
                  : 'border-green-200 bg-green-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <code className="text-lg font-bold font-mono text-ink">
                      {inviteCode.code}
                    </code>
                    {inviteCode.usedAt ? (
                      <span className="px-2 py-1 bg-gray-200 text-ink-secondary text-xs font-medium rounded">
                        Used
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-green-200 text-green-800 text-xs font-medium rounded">
                        Available
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-ink-muted mt-1">
                    Created {new Date(inviteCode.createdAt).toLocaleDateString()}
                    {inviteCode.usedBy && (
                      <span>
                        {' • Used by '}
                        <span className="font-medium">{inviteCode.usedBy.displayName || inviteCode.usedBy.username}</span>
                        {' on '}{new Date(inviteCode.usedAt!).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleCopy(inviteCode.code, inviteCode.id)}
                    className="p-2 text-ink-muted hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                    title="Copy code"
                  >
                    {copiedId === inviteCode.id ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                  {!inviteCode.usedAt && (
                    <button
                      onClick={() => handleDelete(inviteCode.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Delete code"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
